import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import { ArticleHero } from "../ArticleHero";
import safetyHeroImage from "../../assets/images/newborn-photography-safety-supported-pose-hero.webp";
import newbornAwakeImage from "../../assets/images/newborn-awake-pink-wrap-portrait.webp";
import newbornGreenImage from "../../assets/images/newborn-green-stroller-portrait.webp";
import newbornBearImage from "../../assets/images/newborn-sleeping-bear-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { GuideCtaActions } from "./components/GuideCtaActions";
import { UiIcon } from "./components/UiIcons";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./NewbornPhotographySafety.scss";

const safetyPrinciples = [
  {
    number: "01",
    title: "Pause",
    description: "If your baby resists, becomes unsettled or needs to feed, we stop and respond before continuing.",
  },
  {
    number: "02",
    title: "Support",
    description: "A newborn’s head, neck and body stay supported, with an adult close enough to assist immediately.",
  },
  {
    number: "03",
    title: "Composite",
    description:
      "When a finished portrait requires it, supported frames are combined so hands are removed only in editing.",
  },
];

const safeSessionPractices = [
  "No pose or prop setup is guaranteed before we see what feels comfortable for your baby.",
  "Awake photographs are welcome; sleep is never forced or treated as the only path to a beautiful gallery.",
  "Feeding, changing, soothing and parent-cuddle breaks are expected parts of the session.",
  "Sibling portraits are carefully guided and adjusted to the older child’s age and ability to follow directions.",
];

const parentQuestions = [
  "How do you support a baby’s head and neck during posing?",
  "Do you use composite images for poses that need hands-on support?",
  "What happens if my baby is awake, crying or does not settle?",
  "Can I decline a pose or prop at any time?",
  "How are fabrics, wraps and frequently used surfaces prepared between sessions?",
];

const relatedGuides = [
  {
    title: "Best Age for Newborn Photos",
    href: "/resources/newborn/best-age-for-newborn-photos/",
    image: newbornAwakeImage,
    imageAlt: "Awake newborn baby wrapped in soft pink fabric",
  },
  {
    title: "What Happens During a Newborn Session",
    href: "/resources/newborn/what-happens-during-a-newborn-session/",
    image: newbornGreenImage,
    imageAlt: "Sleeping newborn baby posed in a green wooden stroller",
  },
  {
    title: "How to Prepare for Your Newborn Session",
    href: "/resources/newborn/how-to-prepare-for-your-newborn-session/",
    image: newbornBearImage,
    imageAlt: "Sleeping newborn baby in a warm bear-themed portrait",
  },
];

export const NewbornPhotographySafety = (): ReactElement => (
  <main className="newborn-page newborn-article-page newborn-safety-guide">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Newborn", to: "/resources/newborn/" },
          { label: "Is Newborn Photography Safe?" },
        ]}
        category="Newborn safety guide"
        description="A clear look at gentle posing, hands-on support, composite images and the baby-led decisions that keep comfort first throughout a newborn session."
        imageAlt="Newborn baby supported by adult hands during a safe posed photography setup"
        imagePosition="center 48%"
        imageSrc={safetyHeroImage}
        readingTime="8 min read"
        title="Is Newborn Photography Safe? What Parents Should Know"
        updatedAt="2026-08-22"
        updatedLabel="Published Aug 22, 2026"
      />

      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout newborn-safety-answer">
          <span className="newborn-article-callout__icon">
            <UiIcon name="shield" />
          </span>
          <div>
            <h2>The short answer</h2>
            <p>
              Newborn photography can be safe when the session is planned around the baby—not around completing a pose
              list. A baby should never be forced into a position or left unsupported. Comfort, breathing, skin color,
              temperature and natural movement guide every decision.
            </p>
          </div>
        </aside>

        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#safety-first">The three safety principles</a>
            </li>
            <li>
              <a href="#safe-posing">What safe posing looks like</a>
            </li>
            <li>
              <a href="#composite-images">How composite images work</a>
            </li>
            <li>
              <a href="#session-environment">A comfortable session environment</a>
            </li>
            <li>
              <a href="#questions">Questions parents can ask</a>
            </li>
            <li>
              <a href="#safety-faq">Newborn photography safety FAQ</a>
            </li>
          </ol>
        </nav>

        <section className="newborn-article-section newborn-safety-principles-section" id="safety-first">
          <header>
            <p className="newborn-article-kicker">COMFORT COMES FIRST</p>
            <h2>Pause, support and composite</h2>
            <p>
              These three simple principles shape the entire session. They matter more than any prop, pose or planned
              photograph.
            </p>
          </header>
          <div className="newborn-safety-principles">
            {safetyPrinciples.map((principle) => (
              <article key={principle.title}>
                <span>{principle.number}</span>
                <h3>{principle.title}</h3>
                <p>{principle.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="newborn-article-section newborn-safety-practice" id="safe-posing">
          <div>
            <p className="newborn-article-kicker">BABY-LED POSING</p>
            <h2>What safe posing looks like in practice</h2>
            <p>
              Newborns differ in size, flexibility, feeding rhythm and comfort. A position that works naturally for one
              baby may not suit another, so the session changes with the baby in front of the camera.
            </p>
          </div>
          <ul>
            {safeSessionPractices.map((practice) => (
              <li key={practice}>
                <UiIcon name="check" />
                <span>{practice}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="newborn-safety-composite" id="composite-images">
          <div>
            <p className="newborn-article-kicker">SUPPORTED FROM START TO FINISH</p>
            <h2>How composite images protect the baby</h2>
            <p>
              A composite is one finished photograph built from multiple frames. During every frame, an adult hand can
              support the baby. Those supported frames are then carefully combined in editing so the final portrait
              looks seamless without asking the baby to balance alone.
            </p>
          </div>
          <ol>
            <li>
              <span>1</span>
              <div>
                <strong>Position gently</strong>
                <p>The setup is adjusted to the baby’s natural range of movement.</p>
              </div>
            </li>
            <li>
              <span>2</span>
              <div>
                <strong>Keep hands on</strong>
                <p>Head and body remain supported while each frame is photographed.</p>
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <strong>Finish in editing</strong>
                <p>Support hands are removed digitally—not during the pose.</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="newborn-article-section newborn-safety-environment" id="session-environment">
          <header>
            <p className="newborn-article-kicker">BEYOND POSING</p>
            <h2>A comfortable session environment</h2>
            <p>
              Safety also includes the room, equipment and pace of the session. Small practical choices help keep a
              newborn comfortable while parents stay informed and involved.
            </p>
          </header>
          <div>
            <article>
              <h3>Stable surfaces</h3>
              <p>Sets and posing surfaces are stable, low and supervised at all times.</p>
            </article>
            <article>
              <h3>Clean materials</h3>
              <p>Wraps, fabrics and frequently touched items are prepared between sessions.</p>
            </article>
            <article>
              <h3>Comfortable temperature</h3>
              <p>The room is adjusted with the baby’s clothing and comfort in mind.</p>
            </article>
            <article>
              <h3>Close supervision</h3>
              <p>A parent or assisting adult remains nearby, especially during family and sibling portraits.</p>
            </article>
          </div>
        </section>

        <section className="newborn-article-section newborn-safety-questions" id="questions">
          <div className="newborn-safety-checklist">
            <p className="newborn-article-kicker">BEFORE YOU BOOK</p>
            <h2>Questions parents can ask a newborn photographer</h2>
            <ul>
              {parentQuestions.map((question) => (
                <li key={question}>
                  <UiIcon name="check" />
                  <span>{question}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="newborn-safety-faq" id="safety-faq">
            <p className="newborn-article-kicker">QUICK ANSWERS</p>
            <h2>Newborn photography safety FAQ</h2>
            <details>
              <summary>Do parents stay close during the session?</summary>
              <p>Yes. Parents remain part of the process and can speak up, pause or decline any setup at any time.</p>
            </details>
            <details>
              <summary>Can an older newborn still be photographed safely?</summary>
              <p>Yes. The poses and expectations are adapted to the baby’s age, alertness, size and comfort.</p>
            </details>
            <details>
              <summary>Are props always used?</summary>
              <p>
                No. A complete gallery can be created with simple wraps, blankets and photographs in a parent’s arms.
              </p>
            </details>
            <details>
              <summary>What happens if my baby cries?</summary>
              <p>
                We pause for feeding, changing, soothing or cuddling. Crying is communication, not a problem to work
                through.
              </p>
            </details>
          </div>
        </section>

        <aside className="newborn-safety-medical-note">
          <strong>A note for parents</strong>
          <p>
            This guide explains photography workflow and is not medical advice. Share relevant care instructions or
            health concerns before the session, and always follow guidance from your pediatric healthcare professional.
          </p>
        </aside>

        <section className="newborn-article-cta">
          <div>
            <p className="newborn-article-kicker">A GENTLE, BABY-LED EXPERIENCE</p>
            <h2>Ready to plan your newborn session?</h2>
            <p>
              Review newborn session options and pricing, or contact Alena if you would like to talk through safety,
              timing or your baby’s individual needs before booking.
            </p>
          </div>
          <GuideCtaActions category="newborn" />
        </section>

        <section className="newborn-related-guides">
          <header>
            <h2>Related newborn guides</h2>
            <Link to="/resources/newborn/">
              View all guides <UiIcon name="arrow" />
            </Link>
          </header>
          <div>
            {relatedGuides.map((guide, index) => (
              <Link className="newborn-related-guide" key={guide.href} to={guide.href}>
                <ArticlePhoto
                  alt={guide.imageAlt}
                  className={`newborn-related-guide__photo newborn-related-guide__photo--${index + 1}`}
                  label="PHOTO"
                  priority={false}
                  src={guide.image}
                />
                <span>{guide.title}</span>
                <UiIcon name="arrow" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </article>
  </main>
);

export default NewbornPhotographySafety;
